import { useQuery, useMutation } from '@tanstack/react-query'
import { jobService } from '@/lib/api/services/job'
import type { PaginationParams, JobFilterParams, JobApplication } from '@/lib/api/types'

// Query keys factory
export const jobKeys = {
  all: ['jobs'] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: JobFilterParams) =>
    [...jobKeys.lists(), params, filters] as const,
  searches: () => [...jobKeys.all, 'search'] as const,
  search: (searchTerm: string, params: PaginationParams) =>
    [...jobKeys.searches(), searchTerm, params] as const,
  details: () => [...jobKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
}

/**
 * Hook to fetch jobs with filters and pagination
 */
export const useJobs = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: JobFilterParams = {}
) => {
  return useQuery({
    queryKey: jobKeys.list(params, filters),
    queryFn: () => jobService.filterJobs(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook to search jobs with pagination
 */
export const useSearchJobs = (
  searchTerm: string,
  params: PaginationParams = { page: 1, size: 10 }
) => {
  return useQuery({
    queryKey: jobKeys.search(searchTerm, params),
    queryFn: () => jobService.searchJobs(searchTerm, params),
    enabled: searchTerm.trim().length > 0,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook to fetch a single job by ID
 */
export const useJob = (jobId?: string) => {
  return useQuery({
    queryKey: jobKeys.detail(jobId || ''),
    queryFn: () => {
      if (!jobId) {
        throw new Error('Job ID is required')
      }
      return jobService.getJobById(jobId)
    },
    enabled: !!jobId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch job types
 */
export const useJobTypes = () => {
  return useQuery({
    queryKey: [...jobKeys.all, 'jobTypes'],
    queryFn: () => jobService.getJobTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes - job types don't change often
  })
}

/**
 * Hook to increment job view count
 */
export const useIncrementJobView = () => {
  return useMutation({
    mutationFn: (jobId: string) => jobService.incrementJobView(jobId),
  })
}

/** Hook to apply to a job
 */
export const useApplyToJob = () => {
  return useMutation({
    mutationFn: (applicationData: JobApplication) => jobService.applyToJob(applicationData),
  })
}
